import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Users, AlertTriangle, TrendingUp, ShieldAlert, Loader2, RefreshCw,
  Activity, ChevronDown, Mail, Phone, ArrowRight, Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FACTOR_LABEL = {
  recency_days_since_visit: 'Visit recency',
  completion_rate: 'Completion rate',
  notification_engagement: 'Notification reads',
  credential_lapse: 'Credential lapse',
  no_active_engagement: 'Active cases',
  evv_compliance: 'EVV compliance',
};

const BAND_PILL = {
  high: 'bg-red-50 text-red-700 border-red-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  low: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const ADMIN_NAV = [
  { icon: Activity, label: 'Operations Executive', href: '/operations/executive' },
  { icon: Building2, label: 'Referral Sources', href: '/operations/referral-sources' },
  { icon: ShieldAlert, label: 'EVV Compliance', href: '/admin/evv' },
  { icon: Users, label: 'Clinician Retention', active: true },
];

export default function AdminClinicianRetention() {
  const { getAuthHeader } = useAuth();
  const [window, setWindow] = useState(60);
  const [band, setBand] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/clinician-retention`, {
        ...getAuthHeader(),
        params: { window_days: window, risk_band: band || undefined, limit: 100 },
      });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load retention dashboard');
    } finally {
      setLoading(false);
    }
  }, [window, band, getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const bandCounts = data?.band_counts || { high: 0, medium: 0, low: 0 };
  const total = data?.total_scored || 0;

  const highRisk = useMemo(() => (data?.clinicians || []).filter((c) => c.risk_band === 'high'), [data]);

  return (
    <DashboardShell
      navItems={ADMIN_NAV}
      title="Clinician Retention"
      subtitle="Churn-risk score per clinician. Intervene before they ghost — workforce churn is the #1 cost driver in post-acute."
      testId="admin-clinician-retention-page"
    >
      {/* CONTROLS */}
      <div className="cn-card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="retention-controls">
        <div className="flex items-center gap-2">
          <span className="text-xs text-cn-text-muted font-semibold">Window</span>
          <select value={window} onChange={(e) => setWindow(Number(e.target.value))} className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="retention-window-select">
            {[30, 60, 90, 180].map((d) => <option key={d} value={d}>Last {d} days</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-cn-text-muted font-semibold">Risk band</span>
          <select value={band} onChange={(e) => setBand(e.target.value)} className="border border-cn-border rounded-lg px-2 py-1.5 text-sm bg-white" data-testid="retention-band-select">
            <option value="">All bands</option>
            <option value="high">High only</option>
            <option value="medium">Medium only</option>
            <option value="low">Low only</option>
          </select>
        </div>
        <Button onClick={load} disabled={loading} className="cn-btn cn-btn-ghost !py-2" data-testid="retention-refresh">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {loading && <div className="cn-card p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-cn-primary mx-auto" /></div>}

      {!loading && data && (
        <>
          {/* KPI TILES */}
          <div className="grid sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6" data-testid="retention-kpis">
            <BandTile label="Total scored" value={total} icon={Users} testid="retention-kpi-total" />
            <BandTile label="High risk" value={bandCounts.high} icon={AlertTriangle} tone="red" sub="Score ≥ 70 · intervene now" testid="retention-kpi-high" />
            <BandTile label="Medium" value={bandCounts.medium} icon={TrendingUp} tone="amber" sub="40 ≤ Score < 70" testid="retention-kpi-medium" />
            <BandTile label="Low" value={bandCounts.low} icon={Activity} tone="emerald" sub="Score < 40 · retain" testid="retention-kpi-low" />
          </div>

          {/* HIGH-RISK CARDS */}
          {highRisk.length > 0 && (
            <div className="cn-card p-5 mb-6 bg-red-50/40 border-red-200" data-testid="retention-high-risk-card">
              <h4 className="font-display font-semibold text-base text-cn-text mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                Top high-risk clinicians ({highRisk.length})
              </h4>
              <div className="grid md:grid-cols-2 gap-3">
                {highRisk.slice(0, 6).map((c) => (
                  <div key={c.clinician_id} className="cn-card p-3 bg-white" data-testid={`retention-high-${c.clinician_id}`}>
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-cn-text">{c.name}</p>
                      <span className="text-xs font-bold text-red-700">{c.churn_risk_score.toFixed(0)}</span>
                    </div>
                    <p className="text-xs text-cn-text-muted">{c.top_factors[0]?.explain}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CLINICIAN TABLE */}
          <div className="cn-card overflow-hidden" data-testid="retention-table-card">
            <div className="p-5 border-b border-cn-border">
              <h4 className="font-display font-semibold text-base text-cn-text">Clinicians ({data?.clinicians?.length || 0})</h4>
              <p className="text-xs text-cn-text-muted mt-0.5">Highest churn risk first. Click any row to see the factor breakdown.</p>
            </div>
            {data?.clinicians?.length === 0 ? (
              <div className="p-10 text-center" data-testid="retention-empty">
                <Users className="w-6 h-6 text-cn-text-muted mx-auto mb-2" />
                <p className="text-sm text-cn-text-muted">No clinicians match this filter.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="retention-table">
                  <thead className="bg-cn-bone-50 border-b border-cn-border text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">
                    <tr className="text-left">
                      <th className="px-5 py-3">Clinician</th>
                      <th className="px-3 py-3 text-right">Risk score</th>
                      <th className="px-3 py-3">Band</th>
                      <th className="px-3 py-3">Top driver</th>
                      <th className="px-3 py-3 text-right">Last visit</th>
                      <th className="px-3 py-3 text-right">Active cases</th>
                      <th className="px-3 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-cn-border">
                    {(data?.clinicians || []).map((c) => (
                      <React.Fragment key={c.clinician_id}>
                        <tr
                          className="hover:bg-cn-bone-50/40 cursor-pointer"
                          onClick={() => setExpanded(expanded === c.clinician_id ? null : c.clinician_id)}
                          data-testid={`retention-row-${c.clinician_id}`}
                        >
                          <td className="px-5 py-2.5">
                            <p className="font-semibold text-cn-text">{c.name}</p>
                            <p className="text-[10px] text-cn-text-muted">{c.email}</p>
                          </td>
                          <td className="px-3 py-2.5 text-right font-display font-bold">{c.churn_risk_score.toFixed(0)}</td>
                          <td className="px-3 py-2.5">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold ${BAND_PILL[c.risk_band]}`} data-testid={`retention-band-${c.clinician_id}`}>{c.risk_band}</span>
                          </td>
                          <td className="px-3 py-2.5 text-cn-text-muted text-xs">{c.top_factors[0]?.explain}</td>
                          <td className="px-3 py-2.5 text-right text-cn-text-muted">{c.days_since_last_visit === 999 ? 'never' : `${c.days_since_last_visit}d`}</td>
                          <td className="px-3 py-2.5 text-right text-cn-text-muted">{c.active_engagements}</td>
                          <td className="px-3 py-2.5 text-right">
                            <ChevronDown className={`w-4 h-4 inline transition-transform ${expanded === c.clinician_id ? 'rotate-180' : ''}`} />
                          </td>
                        </tr>
                        {expanded === c.clinician_id && (
                          <tr className="bg-cn-bone-50/40" data-testid={`retention-expanded-${c.clinician_id}`}>
                            <td colSpan={7} className="px-5 py-4">
                              <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-3">Factor breakdown</p>
                              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-3">
                                {Object.entries(c.factors).map(([k, f]) => (
                                  <div key={k} className="cn-card p-3 bg-white" data-testid={`retention-factor-${c.clinician_id}-${k}`}>
                                    <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{FACTOR_LABEL[k] || k}</p>
                                    <div className="flex items-center justify-between mt-1">
                                      <span className="font-semibold text-cn-text">{f.score.toFixed(0)}</span>
                                      <span className="text-[10px] text-cn-text-muted">/100</span>
                                    </div>
                                    <p className="text-xs text-cn-text-muted mt-1">{f.explain}</p>
                                  </div>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <Button className="cn-btn cn-btn-primary !py-1.5 text-xs" data-testid={`retention-action-msg-${c.clinician_id}`}>
                                  <Mail className="w-3.5 h-3.5" /> Send re-engagement message
                                </Button>
                                <Button className="cn-btn cn-btn-ghost !py-1.5 text-xs" data-testid={`retention-action-1to1-${c.clinician_id}`}>
                                  <Phone className="w-3.5 h-3.5" /> Schedule 1:1
                                </Button>
                                <Button className="cn-btn cn-btn-ghost !py-1.5 text-xs" data-testid={`retention-action-offers-${c.clinician_id}`}>
                                  Boost shift offers <ArrowRight className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* MODEL EXPLAINER */}
          <div className="cn-card p-5 mt-6" data-testid="retention-model-explainer">
            <h4 className="font-display font-semibold text-sm text-cn-text mb-2">How the model scores</h4>
            <p className="text-xs text-cn-text-muted mb-3">Heuristic, fully auditable. Each factor returns a 0-100 sub-score, then weighted:</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(data?.weights || {}).map(([k, w]) => (
                <div key={k} className="flex items-center justify-between text-xs">
                  <span className="text-cn-text">{FACTOR_LABEL[k] || k}</span>
                  <span className="font-bold text-cn-primary">{(w * 100).toFixed(0)}%</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </DashboardShell>
  );
}

function BandTile({ label, value, sub, icon: Icon, tone = 'neutral', testid }) {
  const iconCls = {
    neutral: 'text-cn-primary',
    red: 'text-red-600',
    amber: 'text-amber-600',
    emerald: 'text-emerald-600',
  }[tone];
  return (
    <div className="cn-card p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{label}</span>
        <Icon className={`w-4 h-4 ${iconCls}`} />
      </div>
      <p className="font-display font-bold text-3xl text-cn-text dark:text-white">{value}</p>
      {sub && <p className="text-xs text-cn-text-muted mt-1">{sub}</p>}
    </div>
  );
}
